/**
 * Centralised React Query client.
 *
 * Why this lives in its own module:
 *  - **Stable defaults** — every screen inherits the same caching strategy
 *    so we cannot accidentally hammer Supabase from one rogue page.
 *  - **One global error toast** — mutation failures surface to the user via
 *    Sonner without each call site needing its own try/catch.
 *  - **Testable** — the React Query test suite can construct a fresh client
 *    with `createQueryClient()` and zero config drift.
 */

import { QueryClient, MutationCache, QueryCache } from '@tanstack/react-query';
import { toast } from 'sonner';
import { createLogger } from './logger';

const log = createLogger('queryClient');

/**
 * Healthcare apps should not retry forever — a stale-write loop on a
 * patient record is much worse than a clear error message. We retry
 * twice with exponential backoff and then surface the failure.
 */
const MAX_RETRIES = 2;
const RETRY_BASE_MS = 500;

function isAuthError(error: unknown): boolean {
  if (typeof error !== 'object' || error == null) return false;
  const status = (error as { status?: number }).status;
  if (status === 401 || status === 403) return true;
  const message = (error as { message?: string }).message ?? '';
  return /jwt|unauthorized|not authenticated/i.test(message);
}

function describeError(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return 'Unbekannter Fehler';
}

/**
 * Build a fresh QueryClient. Exported as a factory so tests can spin up
 * isolated instances and the production app uses a single shared one.
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // 30s is short enough that screens stay fresh after navigation,
        // long enough that tab-switching does not refetch every list.
        staleTime: 30 * 1000,
        // Garbage collect cached pages 5 minutes after the last consumer
        // unmounts. Keeps memory pressure low on long sessions.
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          if (isAuthError(error)) return false;
          return failureCount < MAX_RETRIES;
        },
        retryDelay: (attempt) => Math.min(RETRY_BASE_MS * 2 ** attempt, 8000),
      },
      mutations: {
        // Mutations are user-initiated; do not retry silently or we may
        // double-write a record (e.g. an invoice).
        retry: false,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Background queries are common; only log them, don't toast.
        log.warn('query failed', {
          key: query.queryKey,
          message: describeError(error),
        });
      },
    }),
    mutationCache: new MutationCache({
      onError: (error) => {
        // Mutations are user-initiated; surface them.
        const message = describeError(error);
        log.error('mutation failed', error);
        toast.error('Aktion fehlgeschlagen', { description: message });
      },
    }),
  });
}
